import { useContext, useEffect, useState } from 'react';
import ChildNameHeading from 'components/SiblingSwitch/ChildNameHeading';
import {
  Backdrop,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardProps,
  CircularProgress,
  Container,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  styled,
} from '@mui/material';
import { useNavigate } from 'react-router';

import { useAppDispatch } from 'store/hooks';
import { AuthContext } from 'utils/AuthContext';
import { ROUTE } from 'route/RouteEnums';
import { getVitalsDetails } from 'store/slices/vitalSlice';
import ProfileCompletionGuard from './ProfileCompletionGuard';
import { getPatientSnapshotDetails, getPostMeetingSummary } from 'store/slices/patientInfoSlice';
import { StyledDialog } from 'theme/index';
import { getProfileStatus } from 'store/slices/userSlice';
import CareLinkTile from 'components/CareCards/CareLinkTile';
import CareTileContent from 'components/CareCards/CareTileContent';

/**
 * MM-1132: Release of Information (ROI) Jotform opened by the Coordinate Care tile.
 * Link given by Dr. Bansal in the ticket; it is the same form as Request Records.
 */
const COORDINATE_CARE_FORM_URL = 'https://form.jotform.com/243566370360153';

/**
 * MM-1132: records-request Jotform opened by the Request Medical Records tile.
 * Link given by Dr. Bansal in the ticket; it is the same form as Coordinate Care.
 */
const MEDICAL_RECORDS_FORM_URL = 'https://form.jotform.com/243566370360153';

const Care = (props: any) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user } = useContext(AuthContext);
  const [vitalDetails, setVitalDetails] = useState<any>(null);
  const patientId = user?.patientId;
  const [vitalsLoading, setVitalsLoading] = useState(false);
  const [status, setStatus] = useState<boolean>(false);
  const [migrated, setMigrated] = useState<boolean>(true);
  const isProfileIncomplete = !status;
  const [profileLoading, setProfileLoading] = useState(true);
  const [mwiseLoading, setMwiseLoading] = useState(true);
  const [mwisePending, setMwisePending] = useState<boolean | null>(null);
  /**
   * MM-1135: whether the patient's first treatment plan is available (post-meeting summary exists).
   * null while loading, so the M-Wise Report card does not flash before the answer is known.
   */
  const [treatmentPlanAvailable, setTreatmentPlanAvailable] = useState<boolean | null>(null);
  const shouldShowDialog =
  status &&
  !migrated &&
  mwisePending === true &&
  !profileLoading &&
  !mwiseLoading;
  const isInitialLoading = profileLoading || vitalsLoading || (status && !migrated && mwisePending === null);

  // 🔁 Reset on patient change
  useEffect(() => {
    if (!patientId) return;

    setStatus(false);
    setMigrated(true);
    setMwisePending(null);
    setVitalDetails(null);
    setTreatmentPlanAvailable(null);

    setProfileLoading(true);
    setMwiseLoading(true);
    setVitalsLoading(true);
    checkProfileStatus();
    getVitalInfo();
    checkTreatmentPlanAvailable();
  }, [patientId]);

  useEffect(() => {
    if (status && migrated === false && mwisePending === null) {
      checkMwiseStatus();
    }
  }, [status, migrated, mwisePending]);


  const getVitalInfo = () => {
    setVitalsLoading(true);
    dispatch(getVitalsDetails(patientId))
      .unwrap()
      .then((response: any) => {
        if (
          response?.status === 200 &&
        (response?.data?.height ||
          response?.data?.weight ||
          response?.data?.bloodPressure1 ||
          response?.data?.bloodPressure2)
        ) {
          setVitalDetails(response?.data);
        } else {
          setVitalDetails(null);
        }
      })
      .catch(() => {
        setVitalDetails(null);
      })
      .finally(() => {
        setVitalsLoading(false); // ✅ correct place
      });
  };

  /**
   * MM-1135: checks whether the first treatment plan is available, using the same rule as the
   * M-Wise result page (post-meeting summary returned with data). On error the plan is treated as
   * not available, so the M-Wise Report card stays visible as before.
   */
  const checkTreatmentPlanAvailable = () => {
    dispatch(getPostMeetingSummary(patientId))
      .unwrap()
      .then((response: any) => {
        setTreatmentPlanAvailable(response?.status === 200 && !!response?.data);
      })
      .catch(() => {
        setTreatmentPlanAvailable(false);
      });
  };

  const checkMwiseStatus = () => {
    setMwiseLoading(true);
    dispatch(getPatientSnapshotDetails(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const isMwisePending =
          response.data.mcat === 'Not Started' ||
          response.data.mcat === 'false';
          setMwisePending(isMwisePending);
        }
      })
      .finally(() => {
        setMwiseLoading(false); // ✅ correct
      });
  };
  const checkProfileStatus = () => {
    setProfileLoading(true);
    dispatch(getProfileStatus(patientId))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          const data = response?.data;
          setMigrated(data?.migrated);
          setMwisePending(null);
          const isComplete =
          data?.patientInfoStatus &&
          data?.guardianInfoStatus &&
          data?.childProviderInfoStatus &&
          data?.childCounselorInfoStatus &&
          data?.pharmacyInfoStatus &&
          data?.paymentInfoStatus;

          setStatus(isComplete);
        }
      })
      .catch((error) => {
        console.error('Profile status check failed:', error);
      })
      .finally(() => {
        setProfileLoading(false);
      });
  };

  const StyleCard = styled(Card)<CardProps>(({ theme }) => ({
    boxShadow: '0px 4px 16px 0px rgba(0, 0, 0, 0.12)',
    border: '1px solid #EFF4FF',
    borderRadius: 12,
    background: '#FFF',
    margin: '12px 16px',
    position: 'relative',
    zIndex: 99,
    padding: 20,
    '& .MuiTypography-root': {
      position: 'relative',
    },
    '& .MuiCardContent-root:last-child': {
      padding: 0,
    },
    /**
     * MM-1132 feedback: tile titles are 17px (was 20px).
     */
    '.MuiTypography-h2': {
      fontSize: 17,
    },
  }));

  const openTreatmentPlan = () => {
    navigate(ROUTE.TREATMENT);
  };

  const openMcatReport = () => {
    navigate(ROUTE.MCAT_RESULT);
  };

  const handleDialogOk = () => {
    navigate(ROUTE.DASHBOARD);
  };

  return (
    <Container component='main' maxWidth='md'>
      <Grid container>
        <Grid item xs={10} display={'flex'} alignItems={'center'}>
          {/* MM-1123: same top space above the heading as the Dashboard (paddingTop 6) */}
          <Typography variant='h5' gutterBottom sx={{ paddingTop: 6 }}>
            {/* MM-1123: child's name with yellow underline, e.g. "Emma’s Care" */}
            <ChildNameHeading title='Care' />
          </Typography>
        </Grid>
        <>
          {isProfileIncomplete && <ProfileCompletionGuard />}

          <Backdrop open={isInitialLoading} sx={{ zIndex: 9999 }}>
            <CircularProgress color="inherit" />
          </Backdrop>

          {/* Loader */}

          {/* Dialog */}
          <StyledDialog
            open={shouldShowDialog } // ✅ show dialog only when needed
            disableEscapeKeyDown
            maxWidth='xs' // ✅ make dialog smaller
            fullWidth
          >
            <DialogTitle
              sx={{
                textAlign: 'center',
                pb: 1,
                fontSize: '1rem',
                fontWeight: 600,
              }}
            >
              Complete Your Onboarding
            </DialogTitle>

            <DialogContent sx={{ textAlign: 'center', p: 2 }}>
              <Typography sx={{ fontSize: '13px' }}>
                Finish your onboarding to access this section.
              </Typography>
            </DialogContent>

            <DialogActions
              sx={{
                justifyContent: 'center',
                pb: 2,
              }}
            >
              <Button
                variant='contained'
                size='small'
                onClick={handleDialogOk}
                sx={{
                  minWidth: 90,
                  px: 1.5,
                  py: 0.5,
                  fontSize: '0.75rem',
                  borderRadius: '16px',
                  textTransform: 'none',
                }}
              >
                OK
              </Button>
            </DialogActions>
          </StyledDialog>
        </>
        <Grid item xs={12}>
          <StyleCard>
            <CardContent>
              <CardMedia
                component={'img'}
                src='/images/Group-9550-1.png'
                alt='card-img'
                sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
              />
              {/* MM-1132: shared Care tile layout (one-line button, below the text on mobile) */}
              <CareTileContent
                title='Treatment Plan'
                description='View your medication management plan, current medications, diagnoses, and lab orders.'
                buttonLabel='VIEW PLAN'
                onClick={openTreatmentPlan}
              />
            </CardContent>
          </StyleCard>
        </Grid>
        {vitalDetails && (
          <Grid item xs={12}>
            <StyleCard>
              <CardContent>
                <CardMedia
                  component={'img'}
                  src='images/solid-spiral.svg'
                  alt='card-img'
                  sx={{
                    width: 228,
                    height: 'auto',
                    position: 'absolute',
                    top: 0,
                    margin: 'auto',
                    left: 0,
                    right: 0,
                  }}
                />

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    {/* MM-1132: tile renamed from "System Vitals" to "Vital Signs" */}
                    <Typography variant='h2'>Vital Signs</Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body1'>Weight</Typography>
                    <Typography variant='subtitle2'>
                      {vitalDetails ? vitalDetails.weight : ''} lb
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body1'>Height</Typography>
                    <Typography variant='subtitle2'>
                      {vitalDetails ? vitalDetails.height : ''} in
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body1'>Systolic Blood Pressure</Typography>
                    <Typography variant='subtitle2'>
                      {vitalDetails ? vitalDetails.bloodPressure1 : ''}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant='body1'>Diastolic Blood Pressure</Typography>
                    <Typography variant='subtitle2'>
                      {vitalDetails ? vitalDetails.bloodPressure2 : ''}
                    </Typography>
                  </Grid>
                </Grid>
              </CardContent>
            </StyleCard>
          </Grid>
        )}
        {/* MM-1132: Coordinate Care and Request Medical Records tiles, after Vital Signs */}
        <CareLinkTile
          title='Coordinate Care'
          description={
            'To coordinate care with your pediatrician, therapist, or another member of your treatment team, ' +
            'complete this Release of Information form.'
          }
          buttonLabel='COMPLETE ROI'
          href={COORDINATE_CARE_FORM_URL}
        />
        <CareLinkTile
          title='Request Medical Records'
          description='Request a copy of your or your child’s medical records for your own use.'
          buttonLabel='REQUEST RECORDS'
          href={MEDICAL_RECORDS_FORM_URL}
        />
        {/* MM-1135: M-Wise Report card is removed once the first treatment plan is available */}
        {migrated === false && treatmentPlanAvailable === false && (
          <Grid item xs={12}>
            <StyleCard>
              <CardContent>
                <CardMedia
                  component={'img'}
                  src='/images/Group-9550-1.png'
                  alt='card-img'
                  sx={{ width: 389, height: 'auto', position: 'absolute', right: 0, top: 0 }}
                />
                {/* MM-1132: shared Care tile layout (one-line button, below the text on mobile) */}
                <CareTileContent
                  title='M-Wise Report'
                  description='Check your latest M-Wise Report.'
                  buttonLabel='SHOW M-WISE REPORT'
                  onClick={openMcatReport}
                />
              </CardContent>
            </StyleCard>
          </Grid>)}
      </Grid>
    </Container>
  );
};

export default Care;
